import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import './OpenClawOnFlux.css';

interface OpenClawOnFluxProps {
  onNavigate?: (pageId: string) => void;
}

export function OpenClawOnFlux({ onNavigate }: OpenClawOnFluxProps) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      title: t('openclaw.step1Title', 'Get a Stratus Node'),
      description: t('openclaw.step1Desc', 'Purchase a Stratus FluxNode on the Flux marketplace. This is your one-time investment that replaces ongoing hosting costs forever.'),
      icon: <Icons.Server size={28} />,
      color: '#2b6cb0',
    },
    {
      title: t('openclaw.step2Title', 'Deploy OpenClaw on Flux'),
      description: t('openclaw.step2Desc', 'Use the Flux marketplace to deploy OpenClaw.ai as a decentralized application. One click, no DevOps required.'),
      icon: <Icons.Cloud size={28} />,
      color: '#00d4aa',
    },
    {
      title: t('openclaw.step3Title', 'Run Your AI Assistant 24/7'),
      description: t('openclaw.step3Desc', 'Your OpenClaw instance runs continuously on decentralized infrastructure. No downtime, no monthly bills, no hardware maintenance.'),
      icon: <Icons.Bot size={28} />,
      color: '#8b5cf6',
    },
  ];

  const comparisons = [
    {
      label: t('openclaw.compUpfront', 'Upfront Cost'),
      miniMac: '~$700',
      stratus: '~$1,200 (FLUX collateral)',
      winner: 'stratus',
    },
    {
      label: t('openclaw.compMonthly', 'Monthly Cost'),
      miniMac: '$15-30/mo (electricity)',
      stratus: '$0',
      winner: 'stratus',
    },
    {
      label: t('openclaw.compHosting', 'Hosting Included'),
      miniMac: t('openclaw.no', 'No'),
      stratus: t('openclaw.yesFree', 'Yes, free forever'),
      winner: 'stratus',
    },
    {
      label: t('openclaw.compRewards', 'Passive Rewards'),
      miniMac: t('openclaw.none', 'None'),
      stratus: t('openclaw.fluxRewards', 'FLUX node rewards'),
      winner: 'stratus',
    },
    {
      label: t('openclaw.compUptime', 'Uptime'),
      miniMac: t('openclaw.uptimeLocal', 'Depends on your power/internet'),
      stratus: t('openclaw.uptimeDecentralized', '99.9%+ decentralized'),
      winner: 'stratus',
    },
    {
      label: t('openclaw.compMaintenance', 'Maintenance'),
      miniMac: t('openclaw.maintenanceManual', 'Manual updates, OS patches'),
      stratus: t('openclaw.maintenanceAuto', 'Auto-managed by Flux'),
      winner: 'stratus',
    },
  ];

  const benefits = [
    {
      icon: <Icons.DollarSign size={24} />,
      title: t('openclaw.benefitFreeHosting', 'Free Hosting Forever'),
      description: t('openclaw.benefitFreeHostingDesc', 'Your Stratus node gives you free compute resources. Deploy OpenClaw and any other app without ever paying hosting fees.'),
      color: '#10b981',
    },
    {
      icon: <Icons.Coins size={24} />,
      title: t('openclaw.benefitEarnRewards', 'Earn While You Run'),
      description: t('openclaw.benefitEarnRewardsDesc', 'Your node earns FLUX rewards daily. Your AI assistant literally pays for itself over time.'),
      color: '#f59e0b',
    },
    {
      icon: <Icons.Shield size={24} />,
      title: t('openclaw.benefitDecentralized', 'Decentralized & Resilient'),
      description: t('openclaw.benefitDecentralizedDesc', 'No single point of failure. Your OpenClaw instance is distributed across the Flux network with automatic failover.'),
      color: '#3b82f6',
    },
    {
      icon: <Icons.Zap size={24} />,
      title: t('openclaw.benefitPerformance', 'Enterprise Performance'),
      description: t('openclaw.benefitPerformanceDesc', 'Stratus nodes provide enterprise-grade specs: 8 cores, 64GB RAM, 1TB SSD. More than enough for OpenClaw and beyond.'),
      color: '#8b5cf6',
    },
  ];

  return (
    <div className="openclaw-page">
      {/* Back button */}
      <button className="openclaw-page__back" onClick={() => onNavigate?.('home')}>
        <Icons.ArrowLeft size={18} />
        {t('openclaw.backHome', 'Back to Dashboard')}
      </button>

      {/* Hero Section */}
      <div className="openclaw-page__hero">
        <div className="openclaw-page__hero-bg" />
        <div className="openclaw-page__hero-content">
          <div className="openclaw-page__hero-badges">
            <span className="openclaw-page__badge openclaw-page__badge--flux">
              <Icons.Flux size={14} /> RunOnFlux
            </span>
            <span className="openclaw-page__badge openclaw-page__badge--ai">
              <Icons.Bot size={14} /> OpenClaw.ai
            </span>
          </div>
          <h1 className="openclaw-page__title">
            {t('openclaw.heroTitle', 'Need an AI Assistant?')}
          </h1>
          <p className="openclaw-page__subtitle">
            {t('openclaw.heroSubtitle', 'Instead of buying a Mac Mini to run OpenClaw, buy a Stratus node and get your hosting for free — forever!')}
          </p>
          <div className="openclaw-page__hero-cta">
            <a
              href="https://home.runonflux.io/apps/marketplace"
              target="_blank"
              rel="noopener noreferrer"
              className="openclaw-page__cta-btn openclaw-page__cta-btn--primary"
            >
              <Icons.Rocket size={18} />
              {t('openclaw.deployNow', 'Deploy on Flux')}
            </a>
            <a
              href="https://openclaw.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="openclaw-page__cta-btn openclaw-page__cta-btn--secondary"
            >
              <Icons.ExternalLink size={18} />
              {t('openclaw.learnMore', 'Learn about OpenClaw')}
            </a>
          </div>
        </div>
      </div>

      {/* Comparison Table: Mac Mini vs Stratus */}
      <section className="openclaw-page__section">
        <h2 className="openclaw-page__section-title">
          <Icons.ArrowRightLeft size={22} />
          {t('openclaw.compTitle', 'Mac Mini vs Stratus Node')}
        </h2>
        <p className="openclaw-page__section-subtitle">
          {t('openclaw.compSubtitle', 'Why a Stratus node is the smarter investment for running your AI assistant')}
        </p>

        <div className="openclaw-page__comparison">
          <div className="openclaw-page__comp-header">
            <div className="openclaw-page__comp-label" />
            <div className="openclaw-page__comp-col openclaw-page__comp-col--mac">
              <Icons.Cpu size={20} />
              Mac Mini
            </div>
            <div className="openclaw-page__comp-col openclaw-page__comp-col--stratus">
              <Icons.Server size={20} />
              Stratus Node
            </div>
          </div>
          {comparisons.map((row, i) => (
            <div key={i} className="openclaw-page__comp-row">
              <div className="openclaw-page__comp-label">{row.label}</div>
              <div className={`openclaw-page__comp-cell ${row.winner === 'mac' ? 'openclaw-page__comp-cell--winner' : ''}`}>
                {row.miniMac}
              </div>
              <div className={`openclaw-page__comp-cell ${row.winner === 'stratus' ? 'openclaw-page__comp-cell--winner' : ''}`}>
                {row.winner === 'stratus' && <Icons.CheckCircle size={14} />}
                {row.stratus}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Steps */}
      <section className="openclaw-page__section">
        <h2 className="openclaw-page__section-title">
          <Icons.Zap size={22} />
          {t('openclaw.howTitle', 'How It Works')}
        </h2>
        <div className="openclaw-page__steps">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`openclaw-page__step ${activeStep === i ? 'openclaw-page__step--active' : ''}`}
              onClick={() => setActiveStep(i)}
            >
              <div className="openclaw-page__step-number" style={{ background: step.color }}>
                {i + 1}
              </div>
              <div className="openclaw-page__step-icon" style={{ color: step.color }}>
                {step.icon}
              </div>
              <h3 className="openclaw-page__step-title">{step.title}</h3>
              <p className="openclaw-page__step-desc">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="openclaw-page__section">
        <h2 className="openclaw-page__section-title">
          <Icons.Star size={22} />
          {t('openclaw.benefitsTitle', 'Why This Combo Is Unbeatable')}
        </h2>
        <div className="openclaw-page__benefits">
          {benefits.map((b, i) => (
            <div key={i} className="openclaw-page__benefit-card">
              <div className="openclaw-page__benefit-icon" style={{ background: `${b.color}20`, color: b.color }}>
                {b.icon}
              </div>
              <h3>{b.title}</h3>
              <p>{b.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ROI Calculator Teaser */}
      <section className="openclaw-page__roi-section">
        <div className="openclaw-page__roi-card">
          <div className="openclaw-page__roi-icon">
            <Icons.Calculator size={32} />
          </div>
          <div className="openclaw-page__roi-content">
            <h3>{t('openclaw.roiTitle', 'Return on Investment')}</h3>
            <p>{t('openclaw.roiDesc', 'A Stratus node costs ~40,000 FLUX in collateral (refundable). You earn ~25-35 FLUX/day in rewards. After 6-12 months, the rewards have paid back your initial investment — and you still own the node + free hosting.')}</p>
            <div className="openclaw-page__roi-stats">
              <div className="openclaw-page__roi-stat">
                <span className="openclaw-page__roi-stat-value">~30</span>
                <span className="openclaw-page__roi-stat-label">FLUX/{t('openclaw.day', 'day')}</span>
              </div>
              <div className="openclaw-page__roi-stat">
                <span className="openclaw-page__roi-stat-value">$0</span>
                <span className="openclaw-page__roi-stat-label">{t('openclaw.monthlyHosting', 'monthly hosting')}</span>
              </div>
              <div className="openclaw-page__roi-stat">
                <span className="openclaw-page__roi-stat-value" style={{ color: '#10b981' }}>&infin;</span>
                <span className="openclaw-page__roi-stat-label">{t('openclaw.freeForever', 'free forever')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="openclaw-page__final-cta">
        <h2>{t('openclaw.ctaTitle', 'Ready to Run Your AI for Free?')}</h2>
        <p>{t('openclaw.ctaDesc', 'Stop paying monthly hosting bills. Get a Stratus node, deploy OpenClaw, and earn passive rewards — all at once.')}</p>
        <div className="openclaw-page__cta-buttons">
          <a
            href="https://home.runonflux.io/apps/marketplace"
            target="_blank"
            rel="noopener noreferrer"
            className="openclaw-page__cta-btn openclaw-page__cta-btn--primary"
          >
            <Icons.Server size={18} />
            {t('openclaw.getStratus', 'Get a Stratus Node')}
          </a>
          <a
            href="https://openclaw.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="openclaw-page__cta-btn openclaw-page__cta-btn--secondary"
          >
            <Icons.Bot size={18} />
            {t('openclaw.discoverOpenClaw', 'Discover OpenClaw.ai')}
          </a>
        </div>
      </section>
    </div>
  );
}

export default OpenClawOnFlux;
